/**
 * AnalyticsDashboard Component
 *
 * Main analytics container with role-based tab navigation
 * - Ops tab: Care Coordinators and Case Managers
 * - Manager tab: Regional Managers
 * - Executive tab: Administrators
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { UserRole } from '~/types/predict';
import { AnalyticsFilters } from '~/types/analytics';
import { getDateRangePreset } from '~/utils/generateMockAnalytics';
import GlobalFilters, { LOCATION_TYPES } from './GlobalFilters';
import OpsDashboard from './OpsDashboard';
import ManagerDashboard from './ManagerDashboard';
import ExecutiveDashboard from './ExecutiveDashboard';

type DashboardTab = 'ops' | 'manager' | 'executive';

const AnalyticsDashboard: React.FC = () => {
  const { currentUser, triggerDemoScenario, resetDemoScenario, switchDemoUser, demoMode, setDemoMode, visiblePrograms } = usePredictContext();
  const [activeTab, setActiveTab] = useState<DashboardTab>('ops');
  const [filters, setFilters] = useState<AnalyticsFilters>({
    facilities: [],
    locationTypes: LOCATION_TYPES.map(lt => lt.value), // Default to all location types
    programs: visiblePrograms, // Default to all visible programs (respects demo mode)
    dateRange: getDateRangePreset('30d'),
    ownerFilter: 'ALL',
  });

  // Sync programs filter when demo mode changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, programs: visiblePrograms }));
  }, [demoMode]);

  // Determine which tabs are visible based on user role
  const visibleTabs = useMemo(() => {
    if (!currentUser) return [];

    const tabs: DashboardTab[] = [];

    // Administrators see all tabs
    if (currentUser.role === UserRole.ADMINISTRATOR) {
      return ['ops', 'manager', 'executive'];
    }

    // Ops tab: Care Coordinators and Case Managers
    if (
      currentUser.role === UserRole.CARE_COORDINATOR ||
      currentUser.role === UserRole.CASE_MANAGER
    ) {
      tabs.push('ops');
    }

    // Manager tab: Regional Managers
    if (currentUser.role === UserRole.REGIONAL_MANAGER) {
      tabs.push('manager');
    }

    return tabs;
  }, [currentUser]);

  // Set default tab when visible tabs change
  React.useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      // Default to the most appropriate tab for the role
      if (currentUser?.role === UserRole.ADMINISTRATOR) {
        setActiveTab('executive'); // Admins default to executive view
      } else {
        setActiveTab(visibleTabs[0] as DashboardTab);
      }
    }
  }, [visibleTabs, activeTab, currentUser]);

  // Get facilities list from current user's client organizations
  const facilities = useMemo(() => {
    if (!currentUser) return [];
    return currentUser.clientOrganizations.map((org) => ({
      id: org.id,
      name: org.name,
    }));
  }, [currentUser]);

  // Initialize filters with user's accessible facilities
  React.useEffect(() => {
    if (currentUser && facilities.length > 0) {
      setFilters(prev => ({
        ...prev,
        facilities: facilities.map(f => f.id),
      }));
    }
  }, [currentUser, facilities]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: DashboardTab) => {
    setActiveTab(newValue);
  };

  const handleFiltersChange = (newFilters: AnalyticsFilters) => {
    setFilters(newFilters);
  };

  if (!currentUser) {
    return (
      <Box sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          No user context available
        </Box>
      </Box>
    );
  }

  if (visibleTabs.length === 0) {
    return (
      <Box sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          No analytics dashboards available for your role
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Analytics
        </Typography>
      </Box>

      {/* Global Filters */}
      <GlobalFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        facilities={facilities}
      />

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {visibleTabs.includes('ops') && (
            <Tab label="Operations" value="ops" />
          )}
          {visibleTabs.includes('manager') && (
            <Tab label="Manager" value="manager" />
          )}
          {visibleTabs.includes('executive') && (
            <Tab label="Executive" value="executive" />
          )}
        </Tabs>
      </Box>

      {/* Dashboard Content */}
      {activeTab === 'ops' && <OpsDashboard filters={filters} />}
      {activeTab === 'manager' && <ManagerDashboard filters={filters} />}
      {activeTab === 'executive' && <ExecutiveDashboard filters={filters} />}
    </Box>
  );
};

export default AnalyticsDashboard;
